import React from 'react';
import { useTranslation } from 'react-i18next';
import { TriangleAlert } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { PriceWarning } from '../import.types';

const FALLBACK: Record<PriceWarning['code'], string> = {
    NO_CURRENCY: '{{field}} "{{value}}" has no currency, so it was not imported. Add a currency column (EUR, GBP…).',
    BAD_CURRENCY: '"{{value}}" is not a currency code (EUR, GBP…), so the price was not imported.',
    UNREADABLE: '{{field}} "{{value}}" could not be read, so it was not imported.',
    UNKNOWN_UNIT: 'Unit "{{value}}" is not known, so the price was imported without one.',
};

/**
 * A product row's price cells the import could not use — shown, never guessed.
 * The upload goes ahead without them.
 */
export const ImportPriceWarning: React.FC<{ warnings: PriceWarning[] }> = ({ warnings }) => {
    const { t } = useTranslation();
    if (!warnings.length) return null;

    const lines = Array.from(
        new Set(
            warnings.map((w) =>
                String(t(`PRODUCT_PRICING.CSV.WARNING_${w.code}`, { field: w.field, value: w.value, defaultValue: FALLBACK[w.code] })),
            ),
        ),
    );
    const onlyUnits = warnings.every((w) => w.code === 'UNKNOWN_UNIT');

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    className="ms-2 inline-flex cursor-help items-center gap-1 text-xs font-normal text-warning [&_svg]:size-3.5"
                    data-testid="import-price-warning"
                >
                    <TriangleAlert />
                    {onlyUnits
                        ? t('PRODUCT_PRICING.CSV.WARNING_LABEL_UNIT', 'Price unit not read')
                        : t('PRODUCT_PRICING.CSV.WARNING_LABEL', 'Price not imported')}
                </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-80">
                <ul className="flex flex-col gap-1">
                    {lines.map((line) => (
                        <li key={line}>{line}</li>
                    ))}
                </ul>
            </TooltipContent>
        </Tooltip>
    );
};
